"use client"

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Field, FieldError, FieldGroup } from "@/components/ui/field"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { useEffect, useState } from "react"

import { ApiService } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { observer } from "mobx-react-lite"
import { toastUtils } from "@/utils/toast-utils"
import { useStore } from "@/app/components/common/store.provider"

const COUNTRY_CODES = [
    { code: "+234", country: "Nigeria", dial: "234" },
    { code: "+233", country: "Ghana", dial: "233" },
    { code: "+254", country: "Kenya", dial: "254" },
    { code: "+1", country: "USA/Canada", dial: "1" },
    { code: "+44", country: "UK", dial: "44" },
    { code: "+91", country: "India", dial: "91" },
] as const

function validatePhone(countryCode: string, number: string): string | null {
    const digits = number.replace(/\D/g, "")
    if (digits.length < 9) return "Enter a valid phone number (at least 9 digits)"
    if (digits.length > 15) return "Phone number is too long"
    return null
}

type OnboardingModalProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess?: () => void
}

export const OnboardingModal = observer(function OnboardingModal({
    open,
    onOpenChange,
    onSuccess,
}: OnboardingModalProps) {
    const { authStore } = useStore()
    const profile = authStore.user?.profile

    const [firstName, setFirstName] = useState(profile?.firstName ?? "")
    const [lastName, setLastName] = useState(profile?.lastName ?? "")
    const [countryCode, setCountryCode] = useState("+234")
    const [phoneNumber, setPhoneNumber] = useState(
        profile?.phone?.replace(/^\+\d+/, "") ?? ""
    )
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; phone?: string }>({})

    useEffect(() => {
        if (open && profile) {
            setFirstName(profile.firstName ?? "")
            setLastName(profile.lastName ?? "")
            const phone = profile.phone ?? ""
            const match = phone.match(/^\+(\d+)(.*)$/)
            if (match) {
                const dial = match[1]
                const num = match[2].replace(/\D/g, "")
                const found = COUNTRY_CODES.find((c) => c.dial === dial)
                if (found) {
                    setCountryCode(found.code)
                    setPhoneNumber(num)
                } else {
                    setPhoneNumber(phone.replace(/\D/g, ""))
                }
            } else {
                setPhoneNumber(phone.replace(/\D/g, ""))
            }
            setErrors({})
        }
    }, [open, profile])

    const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, "")
        setPhoneNumber(v)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const newErrors: typeof errors = {}

        if (!firstName.trim()) newErrors.firstName = "First name is required"
        if (!lastName.trim()) newErrors.lastName = "Last name is required"

        const phoneErr = validatePhone(countryCode, phoneNumber)
        if (phoneErr) newErrors.phone = phoneErr

        setErrors(newErrors)
        if (Object.keys(newErrors).length > 0) return

        const fullPhone = `${countryCode}${phoneNumber.replace(/\D/g, "")}`

        setSaving(true)
        try {
            await ApiService.updateMyProfile({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: fullPhone,
            })
            await authStore.refreshMeFromGraphQL()
            toastUtils.success("Profile updated", "Your details have been saved.")
            onOpenChange(false)
            onSuccess?.()
        } catch (err) {
            toastUtils.error("Update failed", err instanceof Error ? err.message : "Could not save your details.")
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md" showCloseButton={true}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <DialogHeader>
                        <DialogTitle>Complete your profile</DialogTitle>
                        <DialogDescription>
                            Complete your profile to continue with your appointment
                        </DialogDescription>
                    </DialogHeader>

                    <FieldGroup>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Field>
                                <Label htmlFor="onboarding-firstname">First name</Label>
                                <Input
                                    id="onboarding-firstname"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="John"
                                    autoComplete="given-name"
                                    className={errors.firstName ? "border-destructive" : ""}
                                />
                                {errors.firstName && <FieldError>{errors.firstName}</FieldError>}
                            </Field>

                            <Field>
                                <Label htmlFor="onboarding-lastname">Last name</Label>
                                <Input
                                    id="onboarding-lastname"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder="Doe"
                                    autoComplete="family-name"
                                    className={errors.lastName ? "border-destructive" : ""}
                                />
                                {errors.lastName && <FieldError>{errors.lastName}</FieldError>}
                            </Field>
                        </div>

                        <Field>
                            <Label htmlFor="onboarding-phone">Mobile number</Label>
                            <div className="flex gap-2">
                                <Select value={countryCode} onValueChange={setCountryCode}>
                                    <SelectTrigger className="w-[96px] shrink-0">
                                        <SelectValue placeholder="+234" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COUNTRY_CODES.map(({ code }) => (
                                            <SelectItem key={code} value={code}>
                                                {code}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input
                                    id="onboarding-phone"
                                    type="tel"
                                    inputMode="numeric"
                                    value={phoneNumber}
                                    onChange={handlePhoneInput}
                                    placeholder="801 234 5678"
                                    autoComplete="tel-national"
                                    className={`flex-1 ${errors.phone ? "border-destructive" : ""}`}
                                />
                            </div>
                            {errors.phone && <FieldError>{errors.phone}</FieldError>}
                        </Field>
                    </FieldGroup>

                    <Button type="submit" className="w-full" disabled={saving}>
                        {saving ? "Saving..." : "Continue"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    )
})
